import { zeroUTasks } from "routes/regime/plugins/zero-u-wizard/controller/zero-u-tasks"
import { usePlanRoot } from "lib/use-plan-root"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { SuppressTreeBusy } from "routes/regime/plugins/zero-u-wizard/suppress-tree-busy"
import { Box, CircularProgress, Container, Divider, List, Stack, Typography } from "@mui/material"
import { RequestPageRepeat } from "lib/@components/request-page-repeat"
import { ListItemBox } from "lib/@components/ListItemBox"
import { PaginationPortal } from "lib/@components/pagination-portal"
import { TaskEntry } from "routes/regime/plugins/zero-u-wizard/task-entry"
import { FitInRemainingSpace } from "lib/@components/fit-in-remaining-space"
import { Frag } from "lib/@components/slot/frag"
import { RegimeNavigator } from "routes/asset-register/plugins/regime-display/regime-navigator"
import { SearchBoxUsingSearchParam } from "lib/@components/searchBoxUsingSearchParam"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { Else } from "lib/@components/else"
import { If } from "lib/@components/switch"
import { NoResults } from "lib/@components/no-results/no-results"
import { Bound } from "lib/@components/binding/Bound"
import { ApplyAllButton, BatchUpdateCompleted, TaskUpdated } from "routes/regime/plugins/zero-u-wizard/apply-all-button"
import { TieredAccessGate } from "minimals-template/components/guards/tiered-access-gate"
import { OU_WIZARD_ACCESS } from "library/hubspot/tiered-access-demands.mjs"
import { Secure } from "lib/authorization/secure"
import { useReferenceState } from "lib/@hooks/use-reference"
import { PageSize } from "lib/@components/pagination/page-size"

export function ZeroUWizardTab() {
    const regime = usePlanRoot()
    const { parentItem } = useBoundContext()
    const [search] = useSearchParam("search")
    const [take, setTake] = useReferenceState("zero-u-wizard-page-size", 50)
    const [skip, setSkip] = useState(0)
    const [removedTasks, setRemovedTasks] = useState(new Set())
    const prevResultsRef = useRef(null)

    // Suppress tree busy indicator while wizard is mounted
    useLayoutEffect(() => {
        SuppressTreeBusy.setWizardMounted(true)
        return () => SuppressTreeBusy.setWizardMounted(false)
    }, [])

    // Listen for individual task updates and immediately remove them from the displayed list
    // Only do optimistic removal when updating ALL tasks with that name (no specific skill/schedule)
    const handleTaskUpdated = useCallback(({ task, skill, schedule }) => {
        if (!skill && !schedule) {
            setRemovedTasks((prev) => new Set([...prev, task]))
        }
    }, [])
    TaskUpdated.useEvent(handleTaskUpdated, [handleTaskUpdated])

    // Listen for batch update completion and remove all updated tasks from the displayed list
    const handleBatchUpdateCompleted = useCallback(({ updates }) => {
        const tasks = updates?.map((u) => u.task) ?? []
        setRemovedTasks((prev) => new Set([...prev, ...tasks]))
    }, [])
    BatchUpdateCompleted.useEvent(handleBatchUpdateCompleted, [handleBatchUpdateCompleted])

    const { data: results } = zeroUTasks.useResults.status({
        regimeId: regime.id,
        group: parentItem.id,
        search,
    })

    // Only clear optimistic removals for tasks that are confirmed gone from server data
    // This prevents race conditions when user updates multiple rows during a slow refetch
    useEffect(() => {
        if (results && results !== prevResultsRef.current) {
            prevResultsRef.current = results
            if (removedTasks.size > 0) {
                // Get all task names currently in the server data
                const serverTaskNames = new Set(results.summarise?.summary?.map((r) => r.value[0]) ?? [])
                // Only keep removals for tasks that are still in server data
                // (meaning the server hasn't processed that update yet)
                setRemovedTasks((prev) => {
                    const stillNeeded = new Set([...prev].filter((task) => serverTaskNames.has(task)))
                    return stillNeeded.size === prev.size ? prev : stillNeeded
                })
            }
        }
    }, [results, removedTasks.size])

    const hasLoaded = !!results
    const data = useMemo(reformat, [results])
    const tasks = useMemo(summariseByTask, [data])
    // Filter out tasks that have been optimistically removed
    const allTaskList = useMemo(
        () => Object.values(tasks).filter((t) => !removedTasks.has(t.task)),
        [tasks, removedTasks]
    )
    const totalCount = allTaskList.length
    const taskList = useMemo(() => allTaskList.slice(skip, skip + take), [allTaskList, skip, take])

    const bodyText = [
        "You can still edit each 0U task inside its schedule.",
        "Upgrade to Professional to see every Task with an Unspecified Frequency in one " +
            "list, apply bulk edits, and update multiple tasks and schedules in a single action. Contact " +
            "your account manager for details.",
    ]

    return (
        <Secure
            demand={OU_WIZARD_ACCESS}
            fallback={
                <TieredAccessGate
                    title="Unspecified Frequencies Overview"
                    bodyText={bodyText}
                    logEvent={"zero-u-wizard"}
                />
            }
        >
            <Bound search={search} regimeId={regime.id} group={parentItem.id}>
                <Container sx={{ overflowX: "visible" }}>
                    <ListItemBox mt={2} sx={{ whiteSpace: "nowrap" }}>
                        <RegimeNavigator noButtons sx={{ width: "initial", flexShrink: 0 }} />
                        <SearchBoxUsingSearchParam
                            sx={{ width: 1, "& input": { fontSize: 13 }, flex: 1 }}
                            placeholder="Search..."
                        />
                        <If truthy={hasLoaded}>
                            <Typography
                                variant="caption"
                                sx={{
                                    mx: 2,
                                    color: totalCount === 0 ? "success.main" : "text.secondary",
                                }}
                            >
                                {totalCount.toLocaleString()} task{totalCount !== 1 ? "s" : ""} remaining
                            </Typography>
                        </If>
                        <PaginationPortal />
                    </ListItemBox>
                    <ListItemBox spacing={3} px={2}>
                        <Box width={1 / 4} pl={2}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold" }}>
                                Task
                            </Typography>
                        </Box>
                        <Box width={1 / 5} pl={1} maxWidth={160}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold" }}>
                                Skill
                            </Typography>
                        </Box>
                        <Box width={1 / 4} pl={1}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold" }}>
                                Schedule
                            </Typography>
                        </Box>
                        <Box flexGrow={1} pl={1}>
                            <ListItemBox pr={3}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: "bold" }}>
                                    Assessed Frequency/Duration
                                </Typography>
                                <Box flex={1} />
                                <ApplyAllButton />
                            </ListItemBox>
                        </Box>
                    </ListItemBox>
                    <Divider />
                    <If not truthy={hasLoaded}>
                        <Box pl={5} pt={5}>
                            <ListItemBox>
                                <Box flex={1} />
                                <Stack alignItems="center" spacing={2}>
                                    <CircularProgress color="primary" size={36} />
                                    <Typography variant="body2">Searching for Zero U tasks...</Typography>
                                </Stack>
                                <Box flex={1} />
                            </ListItemBox>
                        </Box>
                        <Else>
                            <FitInRemainingSpace Component={List} leaveSpace={24} sx={{ overflowX: "visible", px: 2 }}>
                                <RequestPageRepeat
                                    fallback={<NoResults />}
                                    Component={Frag}
                                    list={taskList}
                                    total={totalCount}
                                    pageSize={take}
                                    setSkip={setSkip}
                                    refreshKey={search}
                                    showBottom={true}
                                    item={<TaskEntry />}
                                >
                                    <PageSize take={take} setTake={setTake} />
                                </RequestPageRepeat>
                            </FitInRemainingSpace>
                        </Else>
                    </If>
                </Container>
            </Bound>
        </Secure>
    )

    function reformat() {
        if (!results) return []
        return results.summarise.summary.map((result) => {
            const taskObject = result.tasks.items[0]
            return {
                task: result.value[0],
                frequencyPeriod: taskObject?.frequency?.period ?? null,
                frequencyInterval: taskObject?.frequency?.interval ?? null,
                minutes: taskObject?.minutes ?? null,
                skill: result.value[4],
                schedule: taskObject?.schedule,
                taskObject,
                count: result.count,
            }
        })
    }

    function summariseByTask() {
        return Object.fromEntries(
            Object.entries(
                data.groupBy((item) => `${item.task}|${item.frequencyPeriod}|${item.frequencyInterval}|${item.minutes}`)
            )
                .map(([key, list]) => {
                    const first = list[0]
                    return [
                        key,
                        {
                            task: first.task,
                            frequency: {
                                period: first.frequencyPeriod,
                                interval: first.frequencyInterval,
                            },
                            minutes: first.minutes,
                            count: list.reduce((a, c) => a + c.count, 0),
                            list,
                        },
                    ]
                })
                // Sort by task name (case-insensitive) for consistent ordering
                .sort((a, b) => a[1].task.localeCompare(b[1].task, undefined, { sensitivity: "base" }))
        )
    }
}
